import React, { PureComponent } from "react";
import styled, { ThemeProvider } from "styled-components";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import mgLogo from "../../containers/assets/New MG Logo (Icon).png";

// Load Inter + Plus Jakarta Sans once when this module first loads
// so the badge popup can adopt the reference design's typography
// without changing the rest of the app's Mukta Vaani. Guarded so
// hot-reload / repeated imports don't stack duplicate <link>s.
if (typeof document !== "undefined" && !document.getElementById("mg-badge-fonts")) {
  const link = document.createElement("link");
  link.id = "mg-badge-fonts";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap";
  document.head.appendChild(link);
}

// Shared Skills Badge popup used by both:
//   - Instructor Student Spotlight (Most Valuable Teammate → Skills Badge link)
//   - Student badge consent flow (StudentBadgesFlow design step)
//
// Renders a two-column "badge" (previously called "certificate") with
// a sidebar summary and a main content pane containing a radar chart
// of the four interpersonal skills, a strengths summary, and evidence
// quotes from peers.
//
// Props:
//   open              — bool: if false, renders null
//   data              — insights payload; expects insight_json.strengths.{summary, skills}
//   loading           — bool
//   error             — string | null
//   studentName       — string
//   teammateCount     — number (denominator for the radar's "% of teammates")
//   onClose           — function; when provided, backdrop click + close X close the popup
//   footer            — optional React node; rendered below the main content (used by
//                       the student flow for Accept / Decline / Not Sure Yet buttons)
//   personalize       — optional function(text) => text; substitutes redaction tokens
//                       with the student's name. Defaults to identity.

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${p => (p.theme && p.theme.railBg) || "rgba(15, 23, 42, 0.5)"};
  display: flex;
  align-items: center;
  justify-content: center;
  /* Above the app topbar (App.scss .header is at z-index 3000) so
     the badge popup covers the header for the in-app instructor +
     student views. Higher than any other popup on the dashboard. */
  z-index: 3500;
`;

/* Outer wrapper that lines the badge Card up next to the optional
   DecisionPanel side-by-side. Owns the viewport-budget so the two
   siblings can share it. Falls back to just the Card when no
   decision is required (e.g. instructor Certificate view). */
const PopupWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  max-width: 96vw;
  max-height: 92vh;
`;

const Card = styled.div`
  background: ${p => p.theme.cardBg};
  border-radius: 24px;
  width: 1180px;
  min-width: 0;
  max-height: 92vh;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  box-shadow: ${p => p.theme.cardShadowOuter};
  font-family: 'Inter', 'Mukta Vaani', sans-serif;
  color: ${p => p.theme.ink};
  position: relative;
  flex: 0 1 auto;
`;

/* Floating decision panel that sits to the right of the badge card.
   Deliberately styled as a separate white card with its own shadow
   so the student reads it as "here is where I decide", visually
   detached from the badge itself. */
const DecisionPanel = styled.div`
  width: 240px;
  flex: 0 0 240px;
  align-self: center;
  background: #ffffff;
  border-radius: 14px;
  padding: 22px 20px;
  box-shadow: 0 22px 70px rgba(0, 0, 0, 0.28);
  font-family: 'Mukta Vaani', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DecisionPrompt = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.94);
  line-height: 24px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const DecisionPromptDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #F47C71;
  flex: 0 0 auto;
  margin-top: 8px;
`;

/* Stacks the decision buttons vertically and stretches every child
   (including AcceptButtonWrap-style wrappers) to the panel width so
   the two options read as parallel choices. */
const DecisionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  > * {
    width: 100%;
  }

  button {
    width: 100%;
    justify-content: center;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 18px;
  z-index: 5;
  background: none;
  border: 0;
  color: rgba(0, 27, 59, 0.55);
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  &:hover { color: rgba(0, 27, 59, 0.94); }
`;

/* -------------------------------------------------------------------
 * New badge design (Career Readiness redesign)
 * Palette + typography lifted from the standalone badge.marketgames.io
 * design system: purple accent (#665ba3), navy ink (#273654), Inter for
 * body text, Plus Jakarta Sans (letter-spacing: -0.035em) for headings.
 * ---------------------------------------------------------------- */

const BADGE_FONT_BODY = "'Inter', 'Mukta Vaani', sans-serif";
const BADGE_FONT_HEAD = "'Plus Jakarta Sans', 'Mukta Vaani', sans-serif";

// Purple is the default palette. Gold is scoped to Most Valuable
// Teammate badges. Well Rounded / Market Master keep purple. Colors
// mirror the standalone badge.marketgames.io CSS one-to-one so the
// modal and public card stay visually consistent.
const PURPLE_THEME = {
  ink: "#273654",
  muted: "#69758b",
  accent: "#665ba3",
  kicker: "#665ba3",
  railBg: "#eff0f7",
  cardBg: "#ffffff",
  cardBorder: "#eeebf5",
  cardShadowOuter: "0 22px 60px rgba(35, 35, 73, 0.13), 0 2px 7px rgba(35, 35, 73, 0.05)",
  cardShadowInner: "0 14px 35px rgba(39, 54, 84, 0.1), 0 2px 3px rgba(39, 54, 84, 0.03)",
  aboutBg: "#ffffff",
  aboutBorder: "#e0e1eb",
  divider: "#d9dbe8",
  pillBg: "#f1eef8",
  pillText: "#665b9b",
  logoBg: "#f7f5fa",
  rule: "#e9e9ef",
  observationText: "#69758b",
  skillsBoxBg: "#f5f4f9",
  skillsTitleBg: "#e8e3f3",
  skillsTitleText: "#665b9b",
  axisText: "#56647c",
  assessmentText: "#5e6a7f",
  feedbackBorder: "#786daf",
  feedbackText: "#68748b",
  footerBorder: "#ecebf2",
  footerText: "#8790a3",
  radarStroke: "#7B6BB0",
  radarFill: "#7B6BB0",
  radarGrid: "#c8c3d9",
  spotlightIconBg: "#e8e3f3",
  spotlightIconText: "#665ba3",
  spotlightInnerBg: "#ffffff",
  spotlightInnerBorder: "#eeebf5",
};

const GOLD_THEME = {
  ink: "#342b1d",
  muted: "#74664e",
  accent: "#8c5e0a",
  kicker: "#80560d",
  railBg: "#f3ead8",
  cardBg: "#fffdf8",
  cardBorder: "#eee4d0",
  cardShadowOuter: "0 22px 60px rgba(73, 54, 19, 0.14), 0 2px 7px rgba(73, 54, 19, 0.06)",
  cardShadowInner: "0 14px 35px rgba(61, 47, 23, 0.11), 0 2px 3px rgba(61, 47, 23, 0.04)",
  aboutBg: "#fffaf0",
  aboutBorder: "#e8dcc4",
  divider: "#dfd2b9",
  pillBg: "#FBEDD5",
  pillText: "#66450c",
  logoBg: "#fbf4e5",
  rule: "#eee5d5",
  observationText: "#74664e",
  skillsBoxBg: "#FBEDD5",
  skillsTitleBg: "#f7dfa0",
  skillsTitleText: "#68470b",
  axisText: "#665a43",
  assessmentText: "#665a47",
  feedbackBorder: "#c58c1d",
  feedbackText: "#6d604b",
  footerBorder: "#eee5d5",
  footerText: "#796d58",
  radarStroke: "#c58c1d",
  radarFill: "#c58c1d",
  radarGrid: "#dfd2b9",
  spotlightIconBg: "#f7dfa0",
  spotlightIconText: "#8c5e0a",
  spotlightInnerBg: "#fffdf8",
  spotlightInnerBorder: "#eee4d0",
};

const themeForCategory = (category) =>
  category === "most_valuable" ? GOLD_THEME : PURPLE_THEME;

// Left panel. Muted lavender-gray background; hosts the certifier
// summary + About the Market Games Simulation toggle.
const Sidebar = styled.div`
  flex: 0 0 268px;
  background: ${p => p.theme.railBg};
  padding: 43px 25px 30px;
  overflow-y: auto;
  font-family: ${BADGE_FONT_BODY};
  color: ${p => p.theme.ink};
  display: flex;
  flex-direction: column;
`;

const SidebarKicker = styled.div`
  color: ${p => p.theme.kicker};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 11px;
`;

const SidebarInstructor = styled.div`
  font-family: ${BADGE_FONT_HEAD};
  letter-spacing: -0.035em;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.35;
  color: ${p => p.theme.ink};
  margin: 18px 0 5px;
`;

const SidebarInstitution = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: ${p => p.theme.muted};
`;

const SidebarDivider = styled.div`
  margin: 35px 0 22px;
  border-top: 1px solid ${p => p.theme.divider};
`;

const SidebarAboutTitle = styled.div`
  color: ${p => p.theme.ink};
  font-family: ${BADGE_FONT_BODY};
  font-weight: 600;
  font-size: 13px;
  line-height: 1.55;
`;

const SidebarAboutBody = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: ${p => p.theme.muted};
  line-height: 1.6;
`;

// Right / main stage that hosts the badge card.
const BadgeStage = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  overflow-y: auto;
  background: ${p => p.theme.cardBg};
`;

const BadgeCard = styled.div`
  width: 100%;
  max-width: 830px;
  background: ${p => p.theme.cardBg};
  border: 1px solid ${p => p.theme.cardBorder};
  border-radius: 19px;
  box-shadow: ${p => p.theme.cardShadowInner};
  padding: 43px;
  font-family: ${BADGE_FONT_BODY};
  color: ${p => p.theme.ink};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 22px;
`;

const CardHeaderText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const CardKicker = styled.div`
  color: ${p => p.theme.kicker};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 11px;
`;

const CardName = styled.h2`
  font-family: ${BADGE_FONT_HEAD};
  letter-spacing: -0.035em;
  font-size: 37px;
  line-height: 1.18;
  margin: 15px 0 12px;
  font-weight: 700;
  color: ${p => p.theme.ink};
`;

const CardAwardPill = styled.span`
  display: inline-block;
  padding: 7px 12px;
  background: ${p => p.theme.pillBg};
  color: ${p => p.theme.pillText};
  font-size: 12px;
  font-weight: 600;
  border-radius: 999px;
`;

// Horizontal row for the pills under the student name. Lets the
// "Top X%" bubble sit alongside the category pill when the student's
// top_percent qualifies (≤ 10). Wraps on narrow viewports so the
// second pill drops below instead of overflowing.
const CardPillRow = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

// Rank pill: bold "Top X.XX%" value on the left, two-line caption
// ("of their class" / "VERIFIED STUDENT RANK") on the right. Shares
// the category pill's palette so both read as part of the same award
// row.
const CardRankPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  background: ${p => p.theme.pillBg};
  color: ${p => p.theme.pillText};
  border-radius: 999px;
`;

const CardRankPillValue = styled.span`
  font-weight: 700;
  font-size: 14px;
  line-height: 1.1;
`;

const CardRankPillCaption = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
`;

const CardRankPillCaptionTop = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: ${p => p.theme.pillText};
  opacity: 0.85;
`;

const CardRankPillCaptionBottom = styled.span`
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${p => p.theme.pillText};
`;

// Column that stacks the circular logo and the "Market Games" link
// vertically. flex:none so the whole column keeps its intrinsic size
// inside the space-between CardHeader (which also holds the title /
// name / pill on the left).
const CardLogoColumn = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

// 64px logo + 5px spread box-shadow acts as an outer ring in the
// theme's tint. Per the reference CSS: purple gets #f7f5fa, gold
// gets #fbf4e5 (theme.logoBg).
const CardLogoRing = styled.div`
  width: 64px;
  height: 64px;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 50%;
  background: ${p => p.theme.logoBg};
  box-shadow: 0 0 0 5px ${p => p.theme.logoBg};
`;

const CardLogoImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const CardLogoLink = styled.a`
  font-family: ${BADGE_FONT_BODY};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${p => p.theme.kicker};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CardRule = styled.div`
  border-top: 1px solid ${p => p.theme.rule};
  margin: 27px 0 25px;
`;

const ObservationSection = styled.div`
  margin: 0 0 18px;
`;

const ObservationKicker = styled.div`
  color: ${p => p.theme.kicker};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 11px;
`;

const ObservationSkills = styled.p`
  color: ${p => p.theme.observationText};
  font-size: 13px;
  line-height: 1.55;
  margin: 7px 0 19px;
`;

const EvidenceGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(0, 1fr);
  gap: 34px;
  align-items: start;
`;

const SkillsBox = styled.div`
  min-height: 340px;
  border-radius: 13px;
  background: ${p => p.theme.skillsBoxBg};
  padding: 16px;
`;

const SkillsBoxTitle = styled.div`
  display: inline-block;
  color: ${p => p.theme.skillsTitleText};
  background: ${p => p.theme.skillsTitleBg};
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  padding: 5px 9px;
  letter-spacing: 0.02em;
`;

// Alternative to SkillsBox when only ONE strength has count > 0 and
// the radar chart collapses to a single line. Same outer footprint
// so the two-column grid layout is unchanged.
const SkillSpotlightBox = styled.div`
  min-height: 340px;
  border-radius: 13px;
  background: ${p => p.theme.skillsBoxBg};
  padding: 16px;
  display: flex;
  flex-direction: column;
`;

const SkillSpotlightInner = styled.div`
  flex: 1 1 auto;
  background: ${p => p.theme.spotlightInnerBg};
  border-radius: 12px;
  border: 1px solid ${p => p.theme.spotlightInnerBorder};
  padding: 22px 20px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
`;

const SkillSpotlightIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${p => p.theme.spotlightIconBg};
  color: ${p => p.theme.spotlightIconText};
  display: grid;
  place-items: center;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const SkillSpotlightKicker = styled.div`
  color: ${p => p.theme.kicker};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 11px;
`;

const SkillSpotlightName = styled.div`
  font-family: ${BADGE_FONT_HEAD};
  letter-spacing: -0.02em;
  font-size: 22px;
  line-height: 1.25;
  font-weight: 700;
  color: ${p => p.theme.ink};
`;

const AssessmentText = styled.p`
  color: ${p => p.theme.assessmentText};
  font-size: 13px;
  line-height: 1.7;
  margin: 0 0 19px;
`;

const FeedbackBlock = styled.div``;

const FeedbackHeading = styled.h3`
  font-family: ${BADGE_FONT_HEAD};
  letter-spacing: -0.01em;
  font-size: 13px;
  font-weight: 700;
  margin: 0 0 12px;
  color: ${p => p.theme.ink};
`;

const FeedbackList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 8px;
`;

const FeedbackItem = styled.li`
  border-left: 2px solid ${p => p.theme.feedbackBorder};
  padding-left: 10px;
  font-size: 11px;
  line-height: 1.4;
  color: ${p => p.theme.feedbackText};
  font-style: italic;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  border-top: 1px solid ${p => p.theme.footerBorder};
  padding-top: 19px;
  margin-top: 22px;
  color: ${p => p.theme.footerText};
  font-size: 10px;
`;


// -----------------------------------------------------------------
// Data extraction helpers — resilient to backend shape variations.
// -----------------------------------------------------------------

// Resolve the skill name for one row of strengths data. Exported for
// reuse (Insights popup on the instructor dashboard also consumes it).
export function getSkillName(row) {
  if (!row) return "";
  return row.skill_name
    || row.skill
    || row.name
    || row.label
    || (row.skill && row.skill.name)
    || "";
}

// Resolve the count for one row of strengths data. Exported for reuse.
export function getSkillCount(row) {
  if (!row) return 0;
  return Number(
    row.count
    || row.total_student_count
    || row.student_count
    || row.value
    || (row.peers && row.peers.length)
    || 0
  ) || 0;
}

function getStrengths(data) {
  if (!data) return [];
  const candidates = [
    data.insight_json && data.insight_json.strengths && data.insight_json.strengths.skills,
    data.strengths && data.strengths.skills,
    data.strengths,
    data.strength,
    data.skills,
    data.results && data.results.strengths,
    data.data && data.data.strengths,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    if (Array.isArray(candidates[i])) return candidates[i];
  }
  return [];
}

function getSummary(data) {
  if (!data) return null;
  return (data.insight_json && data.insight_json.strengths && data.insight_json.strengths.summary)
    || (data.strengths && data.strengths.summary)
    || data.strength_summary
    || data.strengths_summary
    || data.summary
    || null;
}

// Radar dataset for the 4 interpersonal-skill axes. Each axis renders
// as `count / # of teammates`, capped at 100%.
function getRadarData(strengths, teammateCount) {
  const axes = [
    { label: "Strong Work Ethic", aliases: ["Strong Work Ethics", "Strong Work Ethic", "Work Ethic"] },
    { label: "Communication", aliases: ["Communication", "Effective Communication"] },
    { label: "Supportive", aliases: ["Supportive"] },
    { label: "Problem-Solver", aliases: ["Problem-Solver", "Problem Solver", "Problem Solving"] },
  ];
  const list = Array.isArray(strengths) ? strengths : [];
  const lookupCount = (aliases) => {
    for (let i = 0; i < aliases.length; i += 1) {
      const want = aliases[i];
      const hit = list.find(s => getSkillName(s) === want);
      if (hit) return getSkillCount(hit);
    }
    return 0;
  };
  return axes.map(a => {
    const count = lookupCount(a.aliases);
    const pct = teammateCount > 0
      ? Math.min(100, (count / teammateCount) * 100)
      : 0;
    return { axis: a.label, value: pct, count };
  });
}

// Evidence selection rules (in order of application):
//   1. Consider strengths only (never weaknesses).
//   2. Skip any quote with fewer than MIN_QUOTE_WORDS words.
//   3. Rank skills by supporting_feedback_count descending. Within a
//      skill, rank surviving quotes by length descending (longer first).
//   4. Round-robin across skills tied at the current count. When the
//      tie group is exhausted, cascade to the next-lower count level.
//   5. Hard cap at MAX_EVIDENCE_QUOTES. If fewer than that exist across
//      all skills, show whatever we have.
const MIN_QUOTE_WORDS = 6;
const MAX_EVIDENCE_QUOTES = 4;

function countWords(str) {
  return String(str || "").trim().split(/\s+/).filter(Boolean).length;
}

// Normalize a quote for near-duplicate comparison: lowercase, strip
// punctuation, collapse whitespace. "Allowed him to explain." and
// "allowed him to explain" become equal after normalization.
function normalizeQuote(str) {
  return String(str || "")
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// Two quotes are treated as duplicates when one is a substring of the
// other after normalization. Common with peer feedback: teammates
// sometimes phrase the same observation with slight variations, or the
// same source quote gets attached to multiple skills (see badge
// MG-27191: "allowed him to explain certain situation" appeared under
// both Supportive and Communication).
function isDuplicateOfAny(candidate, existing) {
  const a = normalizeQuote(candidate);
  if (!a) return false;
  return existing.some(e => {
    const b = normalizeQuote(e.quote);
    if (!b) return false;
    return a.includes(b) || b.includes(a);
  });
}

// Collect every quote attached to one skill row. Prior version only
// pulled the first — now we need the full list so we can rank and
// round-robin.
function getSkillQuotes(row) {
  if (!row) return [];
  const ev = row.evidence
    || row.evidences
    || row.quote
    || row.quotes
    || row.feedback
    || row.peer_feedback
    || null;
  const out = [];
  const push = (q) => {
    if (typeof q === "string" && q.trim()) out.push(q);
  };
  if (Array.isArray(ev)) {
    ev.forEach(item => {
      if (typeof item === "string") push(item);
      else if (item && typeof item === "object") push(item.text || item.quote || "");
    });
  } else if (typeof ev === "string") {
    push(ev);
  } else if (ev && typeof ev === "object") {
    push(ev.text || ev.quote || "");
  }
  return out;
}

function getEvidence(strengths) {
  const list = Array.isArray(strengths) ? strengths : [];

  // Build { skill, count, quotes[] }: rank remaining quotes
  // longest-first, drop skills with nothing usable. `applyWordFilter`
  // gates whether the MIN_QUOTE_WORDS length filter is applied.
  const buildSkills = (applyWordFilter) => list
    .map(s => {
      const skill = getSkillName(s);
      const count = getSkillCount(s);
      let quotes = getSkillQuotes(s);
      if (applyWordFilter) {
        quotes = quotes.filter(q => countWords(q) >= MIN_QUOTE_WORDS);
      }
      quotes = quotes.slice().sort((a, b) => b.length - a.length);
      return { skill, count, quotes };
    })
    .filter(s => s.skill && s.quotes.length > 0);

  const out = [];

  // Count-based round-robin: sort skills by count desc, group ties,
  // pull one quote per skill per pass within each tie group, skipping
  // near-duplicates of anything already in `out`. Called once per
  // pass (strict, then backfill) so backfill honors what strict
  // already pulled and doesn't repeat.
  const pull = (skills) => {
    const sorted = skills.slice().sort((a, b) => b.count - a.count);
    let i = 0;
    while (i < sorted.length && out.length < MAX_EVIDENCE_QUOTES) {
      const currentCount = sorted[i].count;
      let j = i;
      while (j < sorted.length && sorted[j].count === currentCount) j += 1;
      const tieGroup = sorted.slice(i, j).map(s => ({
        skill: s.skill,
        quotes: s.quotes,
        cursor: 0,
      }));

      let pulledThisPass = true;
      while (out.length < MAX_EVIDENCE_QUOTES && pulledThisPass) {
        pulledThisPass = false;
        for (let k = 0; k < tieGroup.length && out.length < MAX_EVIDENCE_QUOTES; k += 1) {
          const t = tieGroup[k];
          while (
            t.cursor < t.quotes.length
            && isDuplicateOfAny(t.quotes[t.cursor], out)
          ) {
            t.cursor += 1;
          }
          if (t.cursor < t.quotes.length) {
            out.push({ skill: t.skill, quote: t.quotes[t.cursor] });
            t.cursor += 1;
            pulledThisPass = true;
          }
        }
      }
      i = j;
    }
  };

  // Strict pass: only quotes >= MIN_QUOTE_WORDS. These are the
  // preferred, substantive quotes for the badge.
  pull(buildSkills(true));

  // Backfill pass: if the strict pass left slots open (i.e. the badge
  // earned few long quotes), top up from the unfiltered list so short
  // quotes still make it in rather than leaving a nearly-empty
  // Teammate Feedback section. Duplicate detection in pull() ensures
  // strict-pass quotes don't get pulled again from the unfiltered
  // build.
  if (out.length < MAX_EVIDENCE_QUOTES) {
    pull(buildSkills(false));
  }

  return out;
}

class SkillsBadgePopup extends PureComponent {
  render() {
    const {
      open,
      data,
      loading,
      error,
      studentName,
      teammateCount,
      onClose,
      footer,
      footerPrompt,
      personalize,
      // Category pill next to the student name — swaps between
      // "Most Valuable Teammate" (default), "Well Rounded Teammate",
      // and "Market Master" depending on which cohort awarded the
      // student their highest total_points.
      categoryLabel,
      // Raw category key ('most_valuable' | 'well_rounded' |
      // 'market_masters'). Drives the color palette: most_valuable
      // swaps to the gold theme; the other two keep purple.
      category,
    } = this.props;

    if (!open) return null;
    const theme = themeForCategory(category);

    const payload = data || {};
    const strengths = getStrengths(payload);
    const sortedStrengths = strengths
      .slice()
      .map(s => ({ name: getSkillName(s), count: getSkillCount(s) }))
      .filter(s => s.name)
      .sort((a, b) => b.count - a.count);

    const rawSummary = getSummary(payload)
      || (strengths[0] && (strengths[0].summary || strengths[0].text))
      || `${studentName || "This student"} demonstrated standout interpersonal skills this round.`;

    const personalizeFn = typeof personalize === "function"
      ? personalize
      : (t => t);
    const summaryRendered = personalizeFn(rawSummary);

    const evidenceQuotes = getEvidence(strengths)
      .map(q => Object.assign({}, q, { quote: personalizeFn(q.quote) }));

    const radarData = getRadarData(strengths, teammateCount || 0);
    // "Only one skill observed" trigger: radar chart collapses to a
    // straight line when only one of the four axes has a non-zero
    // value, which reads as empty. Swap in the Skill Spotlight card
    // instead so a single-skill badge still feels earned.
    const observedSkills = sortedStrengths.filter(s => s.count > 0);
    const singleSkillMode = observedSkills.length === 1;
    const spotlightSkill = singleSkillMode ? observedSkills[0] : null;
    const badgeId = payload.certificate_id
      || payload.cert_id
      || payload.badge_id
      || (payload.user_id != null ? `MG-${payload.user_id}` : "MG");

    return (
      <ThemeProvider theme={theme}>
      <Backdrop onClick={onClose}>
        <PopupWrap onClick={e => e.stopPropagation()}>
          <Card>
            {onClose && (
              <CloseButton
                type="button"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </CloseButton>
            )}

            <Sidebar>
              <SidebarKicker>Badge extended by</SidebarKicker>
              <SidebarInstructor>
                {payload.instructor_last_name
                  ? `Professor ${payload.instructor_last_name}`
                  : "Professor"}
              </SidebarInstructor>
              <SidebarInstitution>
                {payload.course_institution || "School of Business"}
              </SidebarInstitution>

              <SidebarDivider />

              <SidebarAboutTitle>About the Market Games Simulation</SidebarAboutTitle>
              <SidebarAboutBody>
                {payload.about_marketgames
                  || "In the Intro to Business Challenge, teams compete for market share and profit in a simulated business environment. They work through market research, competitor data, and financing options, then make decisions that cut across every core business function. After each round, they analyze their financial statements and KPIs to evaluate what worked and sharpen their next set of decisions. Capital is scarce, information is incomplete, and there is no right answer. What the challenge tests is how a student reasons through ambiguity and uncertainty while making decisions with teammates who see the problem differently."}
              </SidebarAboutBody>
            </Sidebar>

            <BadgeStage>
              <BadgeCard>
                <CardHeader>
                  <CardHeaderText>
                    <CardKicker>Badge / Career Readiness</CardKicker>
                    <CardName>{studentName}</CardName>
                    <CardPillRow>
                      <CardAwardPill>
                        {categoryLabel || "Most Valuable Teammate"}
                      </CardAwardPill>
                      {(() => {
                        // Rank pill: visible only when top_percent is a
                        // positive number ≤ 10. The payload for the
                        // dashboard-side instructor view may not carry
                        // top_percent; the Number.isFinite guard
                        // silently skips rendering in that case.
                        const topPct = Number(payload.top_percent);
                        if (!Number.isFinite(topPct) || topPct <= 0 || topPct > 10) return null;
                        return (
                          <CardRankPill>
                            <CardRankPillValue>Top {topPct.toFixed(2)}%</CardRankPillValue>
                            <CardRankPillCaption>
                              <CardRankPillCaptionTop>of their class</CardRankPillCaptionTop>
                              <CardRankPillCaptionBottom>Verified Student Rank</CardRankPillCaptionBottom>
                            </CardRankPillCaption>
                          </CardRankPill>
                        );
                      })()}
                    </CardPillRow>
                  </CardHeaderText>
                  <CardLogoColumn>
                    <CardLogoRing>
                      <CardLogoImg src={mgLogo} alt="Market Games" />
                    </CardLogoRing>
                    <CardLogoLink
                      href="https://www.marketgames.io"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Market Games
                    </CardLogoLink>
                  </CardLogoColumn>
                </CardHeader>

                <CardRule />

                <ObservationSection>
                  <ObservationKicker>Career Readiness Observed</ObservationKicker>
                  <ObservationSkills>
                    {sortedStrengths.length > 0
                      ? sortedStrengths.map(s => s.name.toLowerCase()).join(", ")
                      : ""}
                  </ObservationSkills>
                </ObservationSection>

                <EvidenceGrid>
                  {singleSkillMode ? (
                    <SkillSpotlightBox>
                      <SkillsBoxTitle>Skill Spotlight</SkillsBoxTitle>
                      <SkillSpotlightInner>
                        <SkillSpotlightIcon aria-hidden>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="9" r="5" />
                            <path d="M8 13 L6 22 L12 19 L18 22 L16 13" />
                          </svg>
                        </SkillSpotlightIcon>
                        <SkillSpotlightKicker>Skill Demonstrated</SkillSpotlightKicker>
                        <SkillSpotlightName>{spotlightSkill.name}</SkillSpotlightName>
                      </SkillSpotlightInner>
                    </SkillSpotlightBox>
                  ) : (
                    <SkillsBox>
                      <SkillsBoxTitle>Skills Map</SkillsBoxTitle>
                      <ResponsiveContainer width="100%" height={300}>
                        <RadarChart
                          cx="50%"
                          cy="50%"
                          outerRadius="90%"
                          data={radarData}
                          margin={{ top: 14, right: 90, bottom: 14, left: 90 }}
                        >
                          <PolarGrid stroke={theme.radarGrid} />
                          <PolarAngleAxis
                            dataKey="axis"
                            tick={{ fontSize: 10, fill: theme.axisText, fontFamily: BADGE_FONT_BODY }}
                          />
                          <PolarRadiusAxis
                            angle={90}
                            domain={[0, 100]}
                            tickCount={5}
                            tick={false}
                            axisLine={false}
                          />
                          <Tooltip
                            formatter={(value, name, item) => {
                              const count = item && item.payload && item.payload.count;
                              return [`${Math.round(value)}% (${count} of ${teammateCount || 0})`, "Strength"];
                            }}
                          />
                          <Radar
                            name="Strength"
                            dataKey="value"
                            stroke={theme.radarStroke}
                            fill={theme.radarFill}
                            fillOpacity={0.55}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </SkillsBox>
                  )}

                  <div>
                    {loading && (
                      <AssessmentText>Loading badge…</AssessmentText>
                    )}
                    {error && (
                      <AssessmentText>{error}</AssessmentText>
                    )}
                    {!loading && !error && (
                      <AssessmentText>{summaryRendered}</AssessmentText>
                    )}

                    {evidenceQuotes.length > 0 && (
                      <FeedbackBlock>
                        <FeedbackHeading>
                          Snapshot of teammates feedback
                        </FeedbackHeading>
                        <FeedbackList>
                          {evidenceQuotes.map((q, i) => (
                            <FeedbackItem key={`badge-quote-${i}`}>
                              “{q.quote}”
                            </FeedbackItem>
                          ))}
                        </FeedbackList>
                      </FeedbackBlock>
                    )}
                  </div>
                </EvidenceGrid>

                <CardFooter>
                  <span>Badge ID: {badgeId}</span>
                </CardFooter>
              </BadgeCard>
            </BadgeStage>
          </Card>

          {(footer || footerPrompt) && (
            <DecisionPanel>
              {footerPrompt && (
                <DecisionPrompt>
                  <DecisionPromptDot aria-hidden />
                  <span>{footerPrompt}</span>
                </DecisionPrompt>
              )}
              {footer && <DecisionButtons>{footer}</DecisionButtons>}
            </DecisionPanel>
          )}
        </PopupWrap>
      </Backdrop>
      </ThemeProvider>
    );
  }
}

export default SkillsBadgePopup;



// WEBPACK FOOTER //
// ./src/components/skills-badge/SkillsBadgePopup.js